import {
  AdminGrantResult,
  ApiError,
  ApiErrorInitialState,
  ApiFunctionsStatus,
  City,
  ClientProgramStatus,
  FundingExplorerSearchResponse,
  GrantAudience,
  GrantCluster,
  GrantFinancingType,
  GrantInitialState,
  GrantOpportunityScoreData,
  GrantProvider,
  GrantProviderSummary,
  GrantResult,
  GrantsViewedHistory,
  GrantTag,
  GrantTotalAmounts,
  GrantTotalsInfo,
  IndustryICNPO,
  LegalStructure,
  NPOBeneficiary,
  NPOScope,
  NPOStream,
  Province,
  QueryFundingExplorerProps,
} from '@hellodarwin/core/lib/features/entities';
import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
} from '@reduxjs/toolkit';
import { RootState } from '../../../app/app-store';
import showErrorNotification from '../../utils/show-error-notifications';
import ClientApi from '../client-api';
import ClientApiV2 from '../client-api-v2';
import {
  subscribeNotifications,
  unsubscribeNotifications,
} from './grants-notifications-slice';
import { createProgram } from './programs-slice';

export const fetchGrant = createAsyncThunk<
  GrantResult,
  { api: ClientApi; grantId: string; locale: string },
  { rejectValue: ApiError }
>(
  'client/fetchGrant',
  async (
    {
      api,
      grantId,
      locale,
    }: { api: ClientApi; grantId: string; locale: string },
    { rejectWithValue },
  ) => {
    try {
      return await api.fetchGrant(grantId, locale);
    } catch (err: any) {
      console.error(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGrantFinancingType = createAsyncThunk<
  GrantFinancingType[],
  { api: ClientApi; locale: string },
  { rejectValue: ApiError }
>(
  'client/fetchGrantFinancingType',
  async (
    { api, locale }: { api: ClientApi; locale: string },
    { rejectWithValue },
  ) => {
    try {
      return await api.fetchGrantFinancingType(locale);
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchIndustriesICNPO = createAsyncThunk<
  IndustryICNPO[],
  { api: ClientApiV2 },
  { rejectValue: ApiError }
>(
  'admin/fetchIndustriesICNPO',
  async ({ api }: { api: ClientApiV2 }, { rejectWithValue }) => {
    try {
      return await api
        .get<IndustryICNPO[]>('/grants/npo/industries')
        .then((res) => res.data);
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchNPOBeneficiaries = createAsyncThunk<
  NPOBeneficiary[],
  { api: ClientApiV2 },
  { rejectValue: ApiError }
>(
  'admin/fetchNPOBeneficiaries',
  async ({ api }: { api: ClientApiV2 }, { rejectWithValue }) => {
    try {
      return await api
        .get<NPOBeneficiary[]>('/grants/npo/beneficiaries')
        .then((res) => res.data);
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchNPOScopes = createAsyncThunk<
  NPOScope[],
  { api: ClientApiV2 },
  { rejectValue: ApiError }
>(
  'admin/fetchNPOScopes',
  async ({ api }: { api: ClientApiV2 }, { rejectWithValue }) => {
    try {
      return await api
        .get<NPOScope[]>('/grants/npo/scopes')
        .then((res) => res.data);
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchNPOStreams = createAsyncThunk<
  NPOStream[],
  { api: ClientApiV2 },
  { rejectValue: ApiError }
>(
  'admin/fetchNPOStreams',
  async ({ api }: { api: ClientApiV2 }, { rejectWithValue }) => {
    try {
      return await api
        .get<NPOStream[]>('/grants/npo/streams')
        .then((res) => res.data);
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchProvinces = createAsyncThunk<
  Province[],
  { api: ClientApi; locale: string },
  { rejectValue: ApiError }
>(
  'client/fetchProvinces',
  async (
    { api, locale }: { api: ClientApi; locale: string },
    { rejectWithValue },
  ) => {
    try {
      return await api.fetchProvinces(locale);
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchCities = createAsyncThunk<
  City[],
  {
    api: ClientApiV2;
    query?: string;
    page?: number;
    limit?: number;
    includeIds?: string[];
    provinceIds?: string[];
  },
  { rejectValue: ApiError }
>(
  'client/fetchCities',
  async (
    {
      api,
      query,
      page,
      limit,
      includeIds,
      provinceIds,
    }: {
      api: ClientApiV2;
      query?: string;
      page?: number;
      limit?: number;
      includeIds?: string[];
      provinceIds?: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams();
      if (query) params.append('query', query);
      if (page !== undefined) params.append('page', page.toString());
      if (limit !== undefined) params.append('limit', limit.toString());
      if (includeIds && includeIds.length > 0)
        params.append('includeIds', includeIds.join(','));
      if (provinceIds && provinceIds.length > 0)
        params.append('provinceIds', provinceIds.join(','));

      const queryString = params.toString();
      const response = await api.get<City[]>(
        `grants/cities${queryString ? `?${queryString}` : ''}`,
      );
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchLegalStructures = createAsyncThunk<
  LegalStructure[],
  { api: ClientApiV2; locale: string },
  { rejectValue: ApiError }
>(
  'client/fetchLegalStructures',
  async (
    { api, locale }: { api: ClientApiV2; locale: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get<LegalStructure[]>(
        `grants/legalStructure?locale=${locale}`,
      );
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGrantProviders = createAsyncThunk<
  GrantProvider[],
  {
    api: ClientApiV2;
    locale: string;
    page?: number;
    limit?: number;
    query?: string;
  },
  { rejectValue: ApiError }
>(
  'client/fetchGrantProviders',
  async (
    {
      api,
      locale,
      page,
      limit,
      query,
    }: {
      api: ClientApiV2;
      locale: string;
      page?: number;
      limit?: number;
      query?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get<GrantProvider[]>(
        `grants/providers?locale=${locale}${page ? `&page=${page}` : ''}${limit ? `&limit=${limit}` : ''}${query ? `&query=${query}` : ''}`,
      );
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGrantProviderSummaries = createAsyncThunk<
  GrantProviderSummary[],
  {
    api: ClientApiV2;
    locale: string;
    page?: number;
    limit?: number;
    query?: string;
    includeIds?: string[];
    orderByGrantCount?: boolean;
  },
  { rejectValue: ApiError }
>(
  'client/fetchGrantProviderSummaries',
  async (
    {
      api,
      locale,
      page,
      limit,
      query,
      includeIds,
      orderByGrantCount,
    }: {
      api: ClientApiV2;
      locale: string;
      page?: number;
      limit?: number;
      query?: string;
      includeIds?: string[];
      orderByGrantCount?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get<GrantProviderSummary[]>(
        `grants/providers/summaries?locale=${locale}${page ? `&page=${page}` : ''}${limit ? `&limit=${limit}` : ''}${query ? `&query=${query}` : ''}${includeIds ? `&includeIds=${includeIds}` : ''}${orderByGrantCount ? `&orderByGrantCount=1` : ''}`,
      );
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGrantAudiences = createAsyncThunk<
  GrantAudience[],
  { api: ClientApiV2; locale: string },
  { rejectValue: ApiError }
>(
  'client/fetchGrantAudiences',
  async (
    { api, locale }: { api: ClientApiV2; locale: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get<GrantAudience[]>(
        `grants/audience?locale=${locale}`,
      );
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchAllCities = createAsyncThunk<
  City[],
  { api: ClientApiV2 },
  { rejectValue: ApiError }
>(
  'client/fetchAllCities',
  async ({ api }: { api: ClientApiV2 }, { rejectWithValue }) => {
    try {
      const response = await api.get<City[]>('grants/cities');
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGrantGoals = createAsyncThunk<
  GrantTag[],
  { api: ClientApiV2; locale: string },
  { rejectValue: ApiError }
>(
  'client/fetchGrantGoals',
  async (
    { api, locale }: { api: ClientApiV2; locale: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get<GrantTag[]>(
        `grants/tags/goal?locale=${locale}`,
      );
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGrantsTotals = createAsyncThunk<
  GrantTotalAmounts[],
  { api: ClientApi },
  { rejectValue: ApiError }
>(
  'client/fetchGrantsTotals',
  async ({ api }: { api: ClientApi }, { rejectWithValue }) => {
    try {
      return await api.fetchGrantsTotals();
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const queryFundingExplorer = createAsyncThunk<
  FundingExplorerSearchResponse,
  {
    api: ClientApi;
    isOnboarding?: boolean;
  } & QueryFundingExplorerProps,
  { rejectValue: ApiError }
>(
  'client/queryFundingExplorer',
  async (
    {
      api,
      ...props
    }: {
      api: ClientApi;
      isOnboarding?: boolean;
    } & QueryFundingExplorerProps,
    { rejectWithValue },
  ) => {
    try {
      return await api.queryFundingExplorer(props);
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);
export const fetchFundingExplorerTotalAmount = createAsyncThunk<
  GrantTotalsInfo,
  {
    api: ClientApi;
  } & QueryFundingExplorerProps,
  { rejectValue: ApiError }
>(
  'client/fetchFundingExplorerTotalAmount',
  async (
    {
      api,
      ...props
    }: {
      api: ClientApi;
    } & QueryFundingExplorerProps,
    { rejectWithValue },
  ) => {
    try {
      return await api.fetchFundingExplorerTotalAmount(props);
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const getFundingExplorerSingleGrant = createAsyncThunk<
  GrantResult,
  { api: ClientApi; grant_id: string; locale: string },
  { rejectValue: ApiError }
>(
  'client/getFundingExplorerSingleGrant',
  async (
    {
      api,
      grant_id,
      locale,
    }: { api: ClientApi; grant_id: string; locale: string },
    { rejectWithValue },
  ) => {
    try {
      return await api.getFundingExplorerSingleGrant(grant_id, locale);
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGrantTags = createAsyncThunk<
  GrantTag[],
  { api: ClientApiV2; grant_id: string; locale: string },
  { rejectValue: ApiError }
>(
  'client/fetchGrantTags',
  async (
    {
      api,
      grant_id,
      locale,
    }: { api: ClientApiV2; grant_id: string; locale: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get<GrantTag[]>(
        `/grants/${grant_id}/tags?locale=${locale}`,
      );
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGrantCluster = createAsyncThunk<
  GrantCluster,
  {
    api: ClientApi;
    grantId: string;
    grantProfileId?: string;
  },
  { rejectValue: ApiError }
>(
  'client/fetchGrantCluster',
  async (
    {
      api,
      grantId,
      grantProfileId,
    }: {
      api: ClientApi;
      grantId: string;
      grantProfileId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await api.fetchGrantCluster(grantId, grantProfileId);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGrantOpportunityScore = createAsyncThunk<
  GrantOpportunityScoreData,
  {
    api: ClientApi;
    grantId: string;
    grantProfileId?: string;
  },
  { rejectValue: ApiError }
>(
  'client/fetchGrantOpportunityScore',
  async (
    {
      api,
      grantId,
      grantProfileId,
    }: {
      api: ClientApi;
      grantId: string;
      grantProfileId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await api.fetchGrantOpportunityScore(grantId, grantProfileId);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGrantsViewedHistory = createAsyncThunk<
  GrantsViewedHistory,
  { api: ClientApi; page: number; size: number; locale: string },
  { rejectValue: ApiError }
>(
  'client/fetchGrantsViewedHistory',
  async (
    {
      api,
      page,
      size,
      locale,
    }: { api: ClientApi; page: number; size: number; locale: string },
    { rejectWithValue },
  ) => {
    try {
      return await api.fetchGrantsViewedHistory(page, size, locale);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

const grantsResultAdapter = createEntityAdapter({
  selectId: (model: GrantResult) => model.grant_id,
});

const adminGrantsAdapter = createEntityAdapter({
  selectId: (model: AdminGrantResult) => model.grant_id,
});

const grantTagsAdapter = createEntityAdapter({
  selectId: (model: GrantTag) => model.id,
});

export interface GrantState {
  status: ApiFunctionsStatus;

  error: ApiError;
  grantsSearch: EntityState<GrantResult, string>;
  grantsSearchTotal: GrantTotalsInfo;
  selectedGrant: GrantResult;
  grant: EntityState<AdminGrantResult, string>;
  grantFinancingType: GrantFinancingType[];
  grantTags: EntityState<GrantTag, string>;
  provinces: Province[];
  legalStructures: LegalStructure[];
  grantTotals: GrantTotalAmounts[];
  providers: GrantProvider[];
  audiences: GrantAudience[];
  cities: City[];
  goals: GrantTag[];
  industriesICNPO: IndustryICNPO[];
  npoBeneficiaries: NPOBeneficiary[];
  npoScopes: NPOScope[];
  npoStreams: NPOStream[];
  // Keyed by grantApplicantReadKey
  grantClusters: Record<string, GrantCluster>;
  opportunityScores: Record<string, GrantOpportunityScoreData>;
  grantClusterLoadingByKey: Record<string, boolean>;
  opportunityScoreLoadingByKey: Record<string, boolean>;
  grantsViewedHistory: GrantsViewedHistory | undefined;
  // Key is grant_id
  createProgramLoadingMap: Record<string, boolean>;
  hasLoadedFundingExplorerResults: boolean;
  latestFundingExplorerQueryRequestId?: string;
  latestFundingExplorerTotalAmountRequestId?: string;
}

const initialState: GrantState = {
  status: {
    general: 'idle',
  },

  error: ApiErrorInitialState,
  grantsSearch: grantsResultAdapter.getInitialState(),
  grantsSearchTotal: { total_funding_max_amount: 0, total_count: 0 },
  selectedGrant: GrantInitialState,
  grant: grantsResultAdapter.getInitialState(),
  grantFinancingType: [],
  grantTags: grantTagsAdapter.getInitialState(),
  provinces: [],
  legalStructures: [],
  grantTotals: [],
  providers: [],
  audiences: [],
  cities: [],
  goals: [],
  industriesICNPO: [],
  npoBeneficiaries: [],
  npoScopes: [],
  npoStreams: [],
  grantClusters: {},
  opportunityScores: {},
  grantClusterLoadingByKey: {},
  opportunityScoreLoadingByKey: {},
  createProgramLoadingMap: {},
  hasLoadedFundingExplorerResults: false,
  latestFundingExplorerQueryRequestId: undefined,
  latestFundingExplorerTotalAmountRequestId: undefined,
  grantsViewedHistory: undefined,
};

/**
 * Cache key for score and cluster reads. Keying by grant + applicant makes
 * staleness unrepresentable: a late response lands in its own slot, and an
 * empty selection has none.
 */
export const grantApplicantReadKey = (
  grantId: string,
  grantProfileId?: string,
) => `${grantId}:${grantProfileId || 'company'}`;

const grantsSlice = createSlice({
  name: 'grants',
  initialState,
  reducers: {
    clearFundingExplorerResults: (state) => {
      grantsResultAdapter.removeAll(state.grantsSearch);
      state.grantsSearchTotal = {
        total_funding_max_amount: 0,
        total_count: 0,
      };
      state.hasLoadedFundingExplorerResults = false;
      state.latestFundingExplorerQueryRequestId = undefined;
      state.latestFundingExplorerTotalAmountRequestId = undefined;
      state.status.queryFundingExplorer = 'idle';
      state.status.fetchFundingExplorerTotalAmount = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchGrant.pending, (state) => {
      state.status['fetchGrant'] = 'pending';
    });
    builder.addCase(fetchGrant.fulfilled, (state, { payload }) => {
      adminGrantsAdapter.upsertOne(state.grantsSearch, payload);
      state.status['fetchGrant'] = 'idle';
    });
    builder.addCase(fetchGrant.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['fetchGrant'] = 'idle';
    });
    builder.addCase(fetchGrantFinancingType.pending, (state) => {
      state.status['fetchGrantFinancingType'] = 'pending';
    });
    builder.addCase(fetchGrantFinancingType.fulfilled, (state, { payload }) => {
      state.grantFinancingType = payload ?? [];
      state.status['fetchGrantFinancingType'] = 'idle';
    });
    builder.addCase(fetchGrantFinancingType.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['fetchGrantFinancingType'] = 'idle';
    });
    builder.addCase(fetchProvinces.pending, (state) => {
      state.status['fetchProvinces'] = 'pending';
    });
    builder.addCase(fetchProvinces.fulfilled, (state, { payload }) => {
      state.provinces = payload;
      state.status['fetchProvinces'] = 'idle';
    });
    builder.addCase(fetchProvinces.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['fetchProvinces'] = 'idle';
    });
    builder.addCase(fetchCities.pending, (state) => {
      state.status['fetchCities'] = 'pending';
    });
    builder.addCase(fetchCities.fulfilled, (state, { payload }) => {
      state.cities = payload;
      state.status['fetchCities'] = 'idle';
    });
    builder.addCase(fetchCities.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['fetchCities'] = 'idle';
    });
    builder.addCase(fetchGrantsTotals.pending, (state) => {
      state.status['fetchGrantsTotals'] = 'pending';
    });
    builder.addCase(fetchGrantsTotals.fulfilled, (state, { payload }) => {
      state.grantTotals = payload;
      state.status['fetchGrantsTotals'] = 'idle';
    });
    builder.addCase(fetchGrantsTotals.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['fetchGrantsTotals'] = 'idle';
    });
    builder.addCase(queryFundingExplorer.pending, (state, { meta }) => {
      state.status['queryFundingExplorer'] = 'pending';
      state.latestFundingExplorerQueryRequestId = meta.requestId;
    });
    builder.addCase(
      queryFundingExplorer.fulfilled,
      (state, { payload, meta }) => {
        if (state.latestFundingExplorerQueryRequestId !== meta.requestId) {
          return;
        }

        state.grantsSearch = grantsResultAdapter.setAll(
          state.grantsSearch,
          payload?.grants ?? [],
        );
        state.grantsSearchTotal = payload.totals;
        state.hasLoadedFundingExplorerResults = true;

        state.status['queryFundingExplorer'] = 'idle';
      },
    );
    builder.addCase(
      queryFundingExplorer.rejected,
      (state, { payload, meta }) => {
        if (state.latestFundingExplorerQueryRequestId !== meta.requestId) {
          return;
        }
        state.error = payload ?? ApiErrorInitialState;
        state.status['queryFundingExplorer'] = 'idle';
      },
    );
    builder.addCase(getFundingExplorerSingleGrant.pending, (state) => {
      state.status['getFundingExplorerSingleGrant'] = 'pending';
    });
    builder.addCase(
      getFundingExplorerSingleGrant.fulfilled,
      (state, { payload }) => {
        state.grantsSearch = grantsResultAdapter.setOne(
          state.grantsSearch,
          payload ? payload : GrantInitialState,
        );
        state.status['getFundingExplorerSingleGrant'] = 'idle';
      },
    );
    builder.addCase(
      getFundingExplorerSingleGrant.rejected,
      (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
        state.status['getFundingExplorerSingleGrant'] = 'idle';
      },
    );
    builder.addCase(
      fetchFundingExplorerTotalAmount.pending,
      (state, { meta }) => {
        state.status['fetchFundingExplorerTotalAmount'] = 'pending';
        state.latestFundingExplorerTotalAmountRequestId = meta.requestId;
      },
    );
    builder.addCase(
      fetchFundingExplorerTotalAmount.fulfilled,
      (state, { payload, meta }) => {
        if (
          state.latestFundingExplorerTotalAmountRequestId !== meta.requestId
        ) {
          return;
        }

        state.grantsSearchTotal = payload;
        state.status['fetchFundingExplorerTotalAmount'] = 'idle';
      },
    );
    builder.addCase(
      fetchFundingExplorerTotalAmount.rejected,
      (state, { payload, meta }) => {
        if (
          state.latestFundingExplorerTotalAmountRequestId !== meta.requestId
        ) {
          return;
        }

        state.error = payload ?? ApiErrorInitialState;
        state.status['fetchFundingExplorerTotalAmount'] = 'idle';
      },
    );
    builder.addCase(fetchGrantTags.pending, (state) => {
      state.status['fetchGrantTags'] = 'pending';
    });
    builder.addCase(fetchGrantTags.fulfilled, (state, { payload }) => {
      grantTagsAdapter.upsertMany(state.grantTags, payload || []);
      state.status['fetchGrantTags'] = 'idle';
    });
    builder.addCase(fetchGrantTags.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['fetchGrantTags'] = 'idle';
    });
    builder.addCase(subscribeNotifications.pending, (state) => {
      state.status['subscribeNotifications'] = 'pending';
    });
    builder.addCase(subscribeNotifications.fulfilled, (state, { payload }) => {
      if (!payload) return;
      for (const subscription of payload) {
        if (subscription.grant_id) {
          grantsResultAdapter.updateOne(state.grantsSearch, {
            id: subscription.grant_id,
            changes: { grant_is_subscribed: true },
          });
        }
      }
      state.status['subscribeNotifications'] = 'idle';
    });
    builder.addCase(subscribeNotifications.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['subscribeNotifications'] = 'idle';
    });
    builder.addCase(unsubscribeNotifications.pending, (state) => {
      state.status['unsubscribeNotifications'] = 'pending';
    });
    builder.addCase(
      unsubscribeNotifications.fulfilled,
      (state, { payload, meta }) => {
        if (!payload) return;
        if (!meta.arg.request.grant_ids) return;
        for (const id of meta.arg.request.grant_ids) {
          if (id && state.grantsSearch.entities[id]) {
            state.grantsSearch.entities[id]!.grant_is_subscribed = false;
          }
        }
        state.status['unsubscribeNotifications'] = 'idle';
      },
    );
    builder.addCase(unsubscribeNotifications.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['unsubscribeNotifications'] = 'idle';
    });
    builder.addCase(fetchLegalStructures.pending, (state) => {
      state.status['fetchLegalStructures'] = 'pending';
    });
    builder.addCase(fetchLegalStructures.fulfilled, (state, { payload }) => {
      state.legalStructures = payload;
      state.status['fetchLegalStructures'] = 'idle';
    });
    builder.addCase(fetchLegalStructures.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['fetchLegalStructures'] = 'idle';
    });
    builder.addCase(fetchGrantProviders.pending, (state) => {
      state.status['fetchGrantProviders'] = 'pending';
    });
    builder.addCase(fetchGrantProviders.fulfilled, (state, { payload }) => {
      state.providers = payload;
      state.status['fetchGrantProviders'] = 'idle';
    });
    builder.addCase(fetchGrantProviders.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['fetchGrantProviders'] = 'idle';
    });

    builder.addCase(fetchGrantProviderSummaries.pending, (state) => {
      state.status['fetchGrantProviderSummaries'] = 'pending';
    });
    builder.addCase(
      fetchGrantProviderSummaries.fulfilled,
      (state, { payload }) => {
        state.providers = payload;
        state.status['fetchGrantProviderSummaries'] = 'idle';
      },
    );
    builder.addCase(
      fetchGrantProviderSummaries.rejected,
      (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
        state.status['fetchGrantProviderSummaries'] = 'idle';
      },
    );

    builder.addCase(fetchGrantAudiences.pending, (state) => {
      state.status['fetchGrantAudiences'] = 'pending';
    });
    builder.addCase(fetchGrantAudiences.fulfilled, (state, { payload }) => {
      state.audiences = payload;
      state.status['fetchGrantAudiences'] = 'idle';
    });
    builder.addCase(fetchGrantAudiences.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['fetchGrantAudiences'] = 'idle';
    });
    builder.addCase(fetchGrantGoals.pending, (state) => {
      state.status['fetchGrantGoals'] = 'pending';
    });
    builder.addCase(fetchGrantGoals.fulfilled, (state, { payload }) => {
      state.goals = payload;
      state.status['fetchGrantGoals'] = 'idle';
    });
    builder.addCase(fetchGrantGoals.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['fetchGrantGoals'] = 'idle';
    });
    builder.addCase(createProgram.pending, (state, { meta }) => {
      const grantId = meta.arg?.program?.program_grant_id as string | undefined;
      if (grantId) {
        state.createProgramLoadingMap[grantId] = true;
      }
    });
    builder.addCase(createProgram.fulfilled, (state, { payload }) => {
      if (payload.program_grant_id) {
        grantsResultAdapter.updateOne(state.grantsSearch, {
          id: payload.program_grant_id,
          changes: {
            client_program_id: payload.program_id,
            client_program_status:
              payload.client_program_status ??
              ClientProgramStatus.ProjectValidationAndEligibility,
          },
        });
        if (state.grantsViewedHistory?.grantsViewed) {
          state.grantsViewedHistory.grantsViewed =
            state.grantsViewedHistory.grantsViewed.map((grant) =>
              grant.grant_id === payload.program_grant_id
                ? {
                    ...grant,
                    client_program_id: payload.program_id,
                    client_program_status:
                      payload.client_program_status ??
                      ClientProgramStatus.ProjectValidationAndEligibility,
                  }
                : grant,
            );
        }
        state.createProgramLoadingMap[payload.program_grant_id] = false;
      }
    });
    builder.addCase(createProgram.rejected, (state, { meta }) => {
      const grantId = meta.arg?.program?.program_grant_id as string | undefined;
      if (grantId) {
        state.createProgramLoadingMap[grantId] = false;
      }
    });
    builder.addCase(fetchGrantsViewedHistory.pending, (state) => {
      state.status['fetchGrantsViewedHistory'] = 'pending';
    });
    builder.addCase(
      fetchGrantsViewedHistory.fulfilled,
      (state, { payload }) => {
        state.grantsViewedHistory = payload;
        state.status['fetchGrantsViewedHistory'] = 'idle';
      },
    );
    builder.addCase(fetchGrantsViewedHistory.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['fetchGrantsViewedHistory'] = 'idle';
    });
    builder.addCase(fetchGrantCluster.pending, (state, { meta }) => {
      state.grantClusterLoadingByKey[
        grantApplicantReadKey(meta.arg.grantId, meta.arg.grantProfileId)
      ] = true;
    });
    builder.addCase(fetchGrantCluster.fulfilled, (state, { payload, meta }) => {
      const key = grantApplicantReadKey(
        meta.arg.grantId,
        meta.arg.grantProfileId,
      );
      if (payload) {
        state.grantClusters[key] = payload;
      } else {
        delete state.grantClusters[key];
      }
      delete state.grantClusterLoadingByKey[key];
    });
    builder.addCase(fetchGrantCluster.rejected, (state, { payload, meta }) => {
      state.error = payload ?? ApiErrorInitialState;
      delete state.grantClusterLoadingByKey[
        grantApplicantReadKey(meta.arg.grantId, meta.arg.grantProfileId)
      ];
    });
    builder.addCase(fetchGrantOpportunityScore.pending, (state, { meta }) => {
      state.opportunityScoreLoadingByKey[
        grantApplicantReadKey(meta.arg.grantId, meta.arg.grantProfileId)
      ] = true;
    });
    builder.addCase(
      fetchGrantOpportunityScore.fulfilled,
      (state, { payload, meta }) => {
        const key = grantApplicantReadKey(
          meta.arg.grantId,
          meta.arg.grantProfileId,
        );
        state.opportunityScores[key] = payload;
        delete state.opportunityScoreLoadingByKey[key];
      },
    );
    builder.addCase(
      fetchGrantOpportunityScore.rejected,
      (state, { payload, meta }) => {
        const key = grantApplicantReadKey(
          meta.arg.grantId,
          meta.arg.grantProfileId,
        );
        // A failed refresh means the cached value's inputs changed under it.
        delete state.opportunityScores[key];
        state.error = payload ?? ApiErrorInitialState;
        delete state.opportunityScoreLoadingByKey[key];
      },
    );
    builder.addCase(fetchIndustriesICNPO.pending, (state) => {
      state.status['fetchIndustriesICNPO'] = 'pending';
    });
    builder.addCase(fetchIndustriesICNPO.fulfilled, (state, { payload }) => {
      state.industriesICNPO = payload ?? [];
      state.status['fetchIndustriesICNPO'] = 'idle';
    });
    builder.addCase(fetchIndustriesICNPO.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['fetchIndustriesICNPO'] = 'idle';
    });
    builder.addCase(fetchNPOBeneficiaries.pending, (state) => {
      state.status['fetchNPOBeneficiaries'] = 'pending';
    });
    builder.addCase(fetchNPOBeneficiaries.fulfilled, (state, { payload }) => {
      state.npoBeneficiaries = payload ?? [];
      state.status['fetchNPOBeneficiaries'] = 'idle';
    });
    builder.addCase(fetchNPOBeneficiaries.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['fetchNPOBeneficiaries'] = 'idle';
    });
    builder.addCase(fetchNPOScopes.pending, (state) => {
      state.status['fetchNPOScopes'] = 'pending';
    });
    builder.addCase(fetchNPOScopes.fulfilled, (state, { payload }) => {
      state.npoScopes = payload ?? [];
      state.status['fetchNPOScopes'] = 'idle';
    });
    builder.addCase(fetchNPOScopes.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['fetchNPOScopes'] = 'idle';
    });
    builder.addCase(fetchNPOStreams.pending, (state) => {
      state.status['fetchNPOStreams'] = 'pending';
    });
    builder.addCase(fetchNPOStreams.fulfilled, (state, { payload }) => {
      state.npoStreams = payload ?? [];
      state.status['fetchNPOStreams'] = 'idle';
    });
    builder.addCase(fetchNPOStreams.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['fetchNPOStreams'] = 'idle';
    });
  },
});

export const { clearFundingExplorerResults } = grantsSlice.actions;

export const selectGrantsIsLoading = createSelector(
  [
    (state: RootState, _?: string) => state.grants.status,
    (_: RootState, functionName?: string) => functionName,
  ],
  (status, functionName) => {
    if (functionName) {
      return status[functionName] === 'pending';
    }
    return !!Object.keys(status).find((s) => s === 'pending');
  },
);

export const selectFundingExplorerTotalsInfo = (state: RootState) =>
  state.grants.grantsSearchTotal;

export const selectFundingExplorerTotalAmount = (state: RootState) =>
  state.grants.grantsSearchTotal.total_funding_max_amount;

export const selectFundingExplorerTotalCount = (state: RootState) =>
  state.grants.grantsSearchTotal.total_count;

export const selectHasLoadedFundingExplorerResults = (state: RootState) =>
  state.grants.hasLoadedFundingExplorerResults;

export const {
  selectById: selectGrantById,
  selectAll: selectAllFundingExplorerGrants,
} = grantsResultAdapter.getSelectors(
  (state: RootState) => state.grants.grantsSearch,
);

export const selectGrantFinancingType = createSelector(
  [(state) => state.grants.grantFinancingType],
  (financingType: GrantFinancingType[]) =>
    financingType?.map((item) => {
      return {
        label: item.type,
        value: item.grant_financing_type_id,
      };
    }),
);

export const selectProvinces = createSelector(
  [(state) => state.grants.provinces],
  (provinces: Province[]) => {
    const uniqueByCode = new Map<string, Province>();
    provinces?.forEach((item) => {
      if (!uniqueByCode.has(item.code)) {
        uniqueByCode.set(item.code, item);
      }
    });
    return Array.from(uniqueByCode.values()).map((item) => {
      return {
        label: item.name,
        value: item.code,
      };
    });
  },
);

export const selectProvincesByLocale = createSelector(
  [
    (state: RootState) => state.grants.provinces,
    (_: RootState, locale: string) => locale,
  ],
  (provinces: Province[], locale: string) => {
    const filtered = provinces?.filter((item) => {
      const itemLocale = (item as Province & { locale?: string }).locale;
      if (!itemLocale) return true;
      return itemLocale === locale;
    });
    const uniqueByCode = new Map<string, Province>();
    filtered?.forEach((item) => {
      if (!uniqueByCode.has(item.code)) {
        uniqueByCode.set(item.code, item);
      }
    });
    return Array.from(uniqueByCode.values()).map((item) => {
      return {
        label: item.name,
        value: item.code,
      };
    });
  },
);
export const selectLegalStructures = createSelector(
  [(state: RootState) => state.grants.legalStructures],
  (legalStructures: LegalStructure[]): { label: string; value: string }[] =>
    legalStructures?.map((item) => {
      return {
        label: item.name,
        value: item.grant_legal_structure_id,
      };
    }),
);

export const selectLegalStructuresMap = createSelector(
  [(state) => state.grants.legalStructures],
  (legalStructures: LegalStructure[]) =>
    legalStructures.reduce<Record<string, LegalStructure>>((acc, item) => {
      acc[item.grant_legal_structure_id] = item;
      return acc;
    }, {}),
);

export const selectGrantProviders = createSelector(
  [(state) => state.grants.providers],
  (providers: GrantProvider[]) =>
    providers?.map((item) => {
      return {
        label: item.title,
        value: item.grant_provider_id,
      };
    }),
);

export const selectGrantProvidersTotalCount = createSelector(
  [(state) => state.grants.providers],
  (providers: GrantProvider[]) => providers?.[0]?.total_count,
);

export const selectGrantAudiences = createSelector(
  [(state) => state.grants.audiences],
  (audiences: GrantAudience[]) =>
    audiences?.map((item) => {
      return {
        label: item.name,
        value: item.grant_audience_id,
      };
    }),
);

export const selectGrantCities = createSelector(
  [(state) => state.grants.cities],
  (cities: City[]) => {
    if (!cities) return [];
    const seen = new Set<string>();
    return cities
      .filter((item) => {
        if (seen.has(item.city)) return false;
        seen.add(item.city);
        return true;
      })
      .map((item) => ({
        label: item.city,
        value: item.city,
        mrc: item.mrc,
      }));
  },
);

export const selectCitiesTotalCount = createSelector(
  [(state) => state.grants.cities],
  (cities: City[]) =>
    (cities?.[0] as (City & { total_count?: number }) | undefined)?.total_count,
);

/** Memoized city→province lookup map for O(1) cross-referencing in city/region sync effects. */
export const selectCityProvinceMap = createSelector(
  [(state) => state.grants.cities],
  (cities: City[]): Map<string, string> => {
    const map = new Map<string, string>();
    cities?.forEach((c) => {
      if (c.province) map.set(c.city, c.province);
    });
    return map;
  },
);

export const selectGrantGoals = createSelector(
  [(state) => state.grants.goals],
  (goals: GrantTag[]) =>
    goals.map((item) => {
      return {
        label: item.content,
        value: item.tag_id,
        groupLabel: item.parent_content,
        target_legal_structure: item.target_legal_structure,
      };
    }),
);

const { selectAll: selectAllGrantTags } = grantTagsAdapter.getSelectors(
  (state: RootState) => state.grants.grantTags,
);

export const selectGrantTags = createSelector(
  selectAllGrantTags,
  (_: RootState, grant_id: string) => grant_id,
  (grantTags: GrantTag[], grant_id: string) =>
    grantTags.filter((item) => item.grant_id === grant_id),
);

export const selectGrantsTotals = (state: RootState) =>
  state.grants.grantTotals;

export const selectGrantsTotalsLoading = (state: RootState) =>
  state.rfp.status === 'pending';

export const selectGrantsLoading = createSelector(
  [
    (state: RootState, _?: string) => state.grants.status,
    (_: RootState, functionName?: string) => functionName,
  ],
  (status: ApiFunctionsStatus, functionName?: string) => {
    if (functionName) {
      return status[functionName] === 'pending';
    }
    return !!Object.keys(status).find((s) => s === 'pending');
  },
);

export const selectIndustriesICNPO = createSelector(
  (state: RootState) => state.grants.industriesICNPO,
  (_: RootState, locale: string) => locale,
  (industriesICNPO, locale) => {
    if (!industriesICNPO) return [];
    return industriesICNPO?.map((item) => {
      return {
        label: locale === 'fr' ? item.title_fr : item.title_en,
        value: item.id,
      };
    });
  },
);

export const selectNPOBeneficiaries = createSelector(
  (state: RootState) => state.grants.npoBeneficiaries,
  (_: RootState, locale: string) => locale,
  (npoBeneficiaries, locale) => {
    if (!npoBeneficiaries) return [];
    return npoBeneficiaries?.map((item) => {
      return {
        label: locale === 'fr' ? item.name_fr : item.name_en,
        value: item.id,
      };
    });
  },
);

export const selectNPOScopes = createSelector(
  (state: RootState) => state.grants.npoScopes,
  (_: RootState, locale: string) => locale,
  (npoScopes, locale) => {
    if (!npoScopes) return [];
    return npoScopes?.map((item) => {
      return {
        label: locale === 'fr' ? item.name_fr : item.name_en,
        value: item.id,
      };
    });
  },
);

export const selectNPOStreams = createSelector(
  (state: RootState) => state.grants.npoStreams,
  (_: RootState, locale: string) => locale,
  (npoStreams, locale) => {
    if (!npoStreams) return [];
    return npoStreams?.map((item) => {
      return {
        label: locale === 'fr' ? item.name_fr : item.name_en,
        value: item.id,
      };
    });
  },
);

export const selectCreateProgramLoading = (state: RootState) =>
  state.grants.createProgramLoadingMap;

export const selectGrantsViewedHistory = (state: RootState) =>
  state.grants.grantsViewedHistory;

export const selectGrantCluster = (
  state: RootState,
  grantId: string,
  grantProfileId?: string,
) => state.grants.grantClusters[grantApplicantReadKey(grantId, grantProfileId)];

export const selectIsGrantClusterLoading = (
  state: RootState,
  grantId: string,
  grantProfileId?: string,
) =>
  !!state.grants.grantClusterLoadingByKey[
    grantApplicantReadKey(grantId, grantProfileId)
  ];

export const selectOpportunityScore = (
  state: RootState,
  grantId: string,
  grantProfileId?: string,
) =>
  state.grants.opportunityScores[
    grantApplicantReadKey(grantId, grantProfileId)
  ];

export const selectIsOpportunityScoreLoading = (
  state: RootState,
  grantId: string,
  grantProfileId?: string,
) =>
  !!state.grants.opportunityScoreLoadingByKey[
    grantApplicantReadKey(grantId, grantProfileId)
  ];

export const grantsReducer = grantsSlice.reducer;
